import { Field, FieldProps } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { Form } from 'react-bootstrap';

export type SelectProps = {
  controlId: string;
  options: SelectOptionType[];
  label?: string;
  className?: string;
  showValidation?: boolean;
  showError?: boolean;
  showEmptyOption?: boolean;
  disabled?: boolean;
  required?: boolean;
};

export type SelectOptionType = {
  label: string;
  value: string;
  orderIndex?: number;
  disabled?: boolean;
};

const orderList = (a: any, b: any) => {
  if (a.orderIndex == null && b.orderIndex == null) {
    // No sort when no OrderIndex
    return 0;
  } else {
    // Order First OrderIndex than Name
    if (a.orderIndex === b.orderIndex && a.label) {
      return a.label.localeCompare(b.label);
    } else {
      return a.orderIndex - b.orderIndex;
    }
  }
};

export const Select: React.FC<React.PropsWithChildren<SelectProps>> = ({
  controlId,
  options,
  label,
  showValidation = true,
  showError = true,
  showEmptyOption = false,
  disabled = false,
  required = false,
  className = '',
}) => {
  const computedOptions = useMemo(
    () =>
      [...options]
        .sort((a, b) => orderList(a, b))
        .map((option, index) => (
          <option value={option.value} key={index} disabled={option.disabled}>
            {option.label}
          </option>
        )),
    [options]
  );

  const selectedOption = useCallback((value: any) => options.filter((option) => option.value === value), [options]);

  return (
    <div className={`select${className ? ` ${className}` : ''}`}>
      <Field name={controlId}>
        {({ field, meta }: FieldProps) => (
          <Form.Group className="form-group" controlId={controlId}>
            {label && (
              <Form.Label className={required ? 'fw-bold' : ''}>
                {label} {required ? ' *' : ''}
              </Form.Label>
            )}
            <Form.Select
              value={field.value || ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              isValid={showValidation && !meta.error && meta.touched}
              isInvalid={showValidation && !!meta.error && meta.touched}
              disabled={disabled}
              title={selectedOption(field.value)[0]?.label || ''}
            >
              <option hidden={true} label="" value="" disabled>
                Select...
              </option>
              {showEmptyOption && <option label=""></option>}
              {computedOptions}
            </Form.Select>
            {showError && <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>}
          </Form.Group>
        )}
      </Field>
    </div>
  );
};
